import React, { memo, useEffect, useMemo, useRef, useCallback, useState } from 'react';
import { t } from 'ttag';
import { formGroup, formControl, FormDataType } from '@core/utils/form';
import { FormInput, FormCheckbox, FormContainer, FormSelect } from '@core/components/form';
import { FCItemControlProps } from '../FCItem';
import { useNonNilObservable } from '@core/utils/hooks/rxjs';
import { FCType } from './types';
import SignatureCanvas from 'react-signature-canvas';
import { Button } from '@core/components/Button';
import { UploadAPI } from '@business/api/upload';
import { tap } from 'rxjs/operators';

export enum SignatureConfiguration {
  textbox = 'textbox',
  signaturePad = 'signaturePad',
}

export function defineSignatureForm() {
  const form = formGroup({
    type: formControl<FCType>({ initialValue: FCType.signature }),
    title: formControl<string>({
      label: t`Title`,
      required: true,
      initialValue: t`Signature`,
    }),
    placeholder: formControl<string>({
      label: t`Placeholder`,
      initialValue: t`Type here to create signature...`,
    }),
    description: formControl<string>({
      label: t`Description`,
    }),
    isRequired: formControl<boolean>({
      label: t`Required`,
      initialValue: false,
    }),
    configuration: formControl<SignatureConfiguration>({
      label: t`Configuration`,
      initialValue: SignatureConfiguration.textbox,
    }),
  });

  return form;
}

export type FCSignatureFormData = FormDataType<typeof defineSignatureForm>;
export type FCSignatureForm = ReturnType<typeof defineSignatureForm>;

export interface FCSignatureProps extends FCItemControlProps {
  form: FCSignatureForm;
}

export const FCSignature = memo((props: FCSignatureProps) => {
  const { form, onLabel } = props;
  const formValue = useNonNilObservable(form.value$);

  useEffect(() => {
    if (formValue.title) {
      onLabel && onLabel(formValue.title);
    }
  }, [formValue, onLabel]);

  const configurationOptions = useMemo(
    () => [
      { label: 'Text Box', value: SignatureConfiguration.textbox },
      { label: 'Signature Pad', value: SignatureConfiguration.signaturePad },
    ],
    [],
  );

  return (
    <FormContainer onSubmit={form.submit}>
      <div className="p-3">
        <FormInput autoFocus control={form.controls.title} />
        <FormInput control={form.controls.placeholder} />
        <FormInput type="textarea" control={form.controls.description} />
        <FormCheckbox control={form.controls.isRequired} />
        <FormSelect control={form.controls.configuration} items={configurationOptions} />
      </div>
    </FormContainer>
  );
});

export interface FCSignaturePreviewProps extends FCSignatureFormData {
  control?: any;
  disabled?: boolean;
}

export const FCSignaturePreview = memo((props: FCSignaturePreviewProps) => {
  const signaturePadRef = useRef<SignatureCanvas>(null);
  const [signatureData, setSignatureData] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [signatureMode, setSignatureMode] = useState<'draw' | 'type'>('draw');
  const [typedSignature, setTypedSignature] = useState<string>('');

  const control =
    props.control || useMemo(() => formControl<string>({ placeholder: props.placeholder, label: props.title, required: props.isRequired }), [props]);

  // Subscribe to control value changes
  const controlValue = useNonNilObservable(control.value$, '');
  // useEffect(() => {
  //   if (control && control.value) {
  //     const value = control.value;
  //     // Check if it's a base64 string or URL
  //     if (value.startsWith('data:image')) {
  //       // It's base64, need to upload it
  //       fetch(value)
  //         .then(res => res.blob())
  //         .then(blob => {
  //           const file = new File([blob], `signature_${Date.now()}.png`, { type: 'image/png' });
  //           setIsUploading(true);

  //           UploadAPI.upload('forms', file, false)
  //             .pipe(
  //               tap(uploadResponse => {
  //                 setSignatureData(uploadResponse.fileUrl);
  //                 control.setValue(uploadResponse.fileUrl);
  //                 setIsUploading(false);
  //               }),
  //             )
  //             .subscribe({
  //               error: () => {
  //                 // If upload fails, keep the base64 for display
  //                 setSignatureData(value);
  //                 setIsUploading(false);
  //               },
  //             });
  //         });
  //     } else {
  //       // It's already a URL
  //       setSignatureData(value);
  //     }
  //   }
  // }, [control]);

  const clearSignature = useCallback(() => {
    signaturePadRef.current?.clear();
    setSignatureData('');
    setTypedSignature('');
    control.setValue('');
  }, [control]);

  // Convert typed text to signature image
  const generateTypedSignature = useCallback((text: string): string => {
    const canvas = document.createElement('canvas');
    canvas.width = 500;
    canvas.height = 150;
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = '#000000';
      ctx.font = '48px Brush Script MT, cursive';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, canvas.width / 2, canvas.height / 2);
    }
    return canvas.toDataURL();
  }, []);

  // Save signature from canvas or typed text to control only when called
  const saveSignatureToControl = useCallback(() => {
    let dataURL = '';
    if (signatureMode === 'draw') {
      if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
        dataURL = signaturePadRef.current.toDataURL();
      }
    } else if (signatureMode === 'type' && typedSignature.trim()) {
      dataURL = generateTypedSignature(typedSignature.trim());
    }

    if (dataURL) {
      control.setValue(dataURL);
      setSignatureData(dataURL);
    }
  }, [control, signatureMode, typedSignature, generateTypedSignature]);

  // Expose save function and validation to parent form
  useEffect(() => {
    // Store the save function on the control so it can be called before form submit
    if (control) {
      (control as any)._saveSignature = saveSignatureToControl;

      // Add custom validator that checks canvas instead of control value
      if (props.isRequired) {
        (control as any)._signatureValidator = () => {
          // If there's already a saved signature, it's valid
          if (control.value) return null;
          // Check if canvas has signature or typed text
          if (signaturePadRef.current && !signaturePadRef.current.isEmpty()) {
            return null; // Valid
          }
          if (typedSignature.trim()) {
            return null; // Valid
          }
          return { required: true }; // Invalid
        };
      }
    }
  }, [control, props.isRequired, saveSignatureToControl]);

  // Check both signatureData state and control value for display
  const displaySignature = signatureData || controlValue;

  if (props.configuration === SignatureConfiguration.signaturePad) {
    // If disabled (view mode) and signature exists, show professional read-only view
    if (props.disabled && displaySignature) {
      return (
        <div style={{ marginBottom: '20px' }}>
          <label style={{ display: 'block', marginBottom: '12px', fontWeight: 600, color: '#212529', fontSize: '14px' }}>{props.title}</label>
          {props.description && <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px', lineHeight: '1.5' }}>{props.description}</p>}
          <div style={{ maxWidth: '32.5%' }}>
            <div
              style={{
                borderBottom: '2px solid #000',
                paddingBottom: '8px',
                marginBottom: '8px',
              }}
            >
              <img
                src={displaySignature}
                alt="Signature"
                style={{
                  maxWidth: '100%',
                  height: 'auto',
                  display: 'block',
                  maxHeight: '80px',
                }}
              />
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d', display: 'flex', alignItems: 'center', gap: '4px' }}>
              <i className="bx bx-check-circle" style={{ color: '#28a745', fontSize: '14px' }}></i>
              <span>Digitally signed</span>
            </div>
          </div>
        </div>
      );
    }

    // Edit mode
    return (
      <div style={{ marginBottom: '20px' }}>
        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#212529', fontSize: '14px' }}>
          {props.title}
          {props.isRequired && <span style={{ color: '#dc3545' }}> *</span>}
        </label>
        {props.description && <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px', lineHeight: '1.5' }}>{props.description}</p>}
        <div style={{ maxWidth: '32.5%' }}>
          {isUploading ? (
            <div style={{ textAlign: 'center', padding: '50px' }}>
              <i className="bx bx-loader bx-spin" style={{ fontSize: '32px' }}></i>
              <p>Uploading signature...</p>
            </div>
          ) : displaySignature ? (
            <div>
              <div
                style={{
                  borderBottom: '2px solid #000',
                  paddingBottom: '8px',
                  marginBottom: '8px',
                }}
              >
                <img
                  src={displaySignature}
                  alt="Signature"
                  style={{
                    maxWidth: '100%',
                    height: 'auto',
                    display: 'block',
                    maxHeight: '80px',
                  }}
                />
              </div>
              {!props.disabled && (
                <div style={{ fontSize: '11px', color: '#6c757d', display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <i className="bx bx-check-circle" style={{ color: '#28a745', fontSize: '14px' }}></i>
                  <span>Signature saved</span>
                </div>
              )}
            </div>
          ) : (
            <div>
              <div style={{ marginBottom: '12px', display: 'flex', gap: '8px' }}>
                <Button
                  extraClassNames={`btn-sm ${signatureMode === 'draw' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setSignatureMode('draw')}
                  intent={signatureMode === 'draw' ? 'primary' : 'secondary'}
                >
                  Draw
                </Button>
                <Button
                  extraClassNames={`btn-sm ${signatureMode === 'type' ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => setSignatureMode('type')}
                  intent={signatureMode === 'type' ? 'primary' : 'secondary'}
                >
                  Type
                </Button>
              </div>

              {signatureMode === 'draw' ? (
                <div style={{ backgroundColor: '#fafafa', borderRadius: '4px', padding: '8px', marginBottom: '12px' }}>
                  <SignatureCanvas
                    ref={signaturePadRef}
                    canvasProps={{
                      width: 500,
                      height: 150,
                      className: 'signature-canvas',
                      style: { border: 'none', borderRadius: '4px', backgroundColor: '#ffffff' },
                    }}
                    backgroundColor="#ffffff"
                    penColor="#000000"
                    velocityFilterWeight={0.7}
                    minWidth={0.5}
                    maxWidth={2.5}
                  />
                </div>
              ) : (
                <div style={{ marginBottom: '12px' }}>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Type your full name"
                    value={typedSignature}
                    onChange={e => setTypedSignature(e.target.value)}
                    style={{
                      fontSize: '24px',
                      fontFamily: 'Brush Script MT, cursive',
                      padding: '20px',
                      textAlign: 'center',
                    }}
                  />
                </div>
              )}

              <Button extraClassNames="btn-outline-secondary btn-sm" onClick={clearSignature} intent="secondary">
                Clear
              </Button>
            </div>
          )}
        </div>
        {/* {!props.disabled && displaySignature && (
          <div style={{ marginTop: '12px' }}>
            <Button extraClassNames="btn-outline-secondary btn-sm" onClick={clearSignature} intent="secondary">
              <i className="bx bx-revision" style={{ marginRight: '4px' }}></i>
              Clear & Re-sign
            </Button>
          </div>
        )} */}
      </div>
    );
  }

  return <FormInput control={control} readonly readOnly />;
});
